import { useState } from 'react';
import { supabase } from '../utils/supabase';
import { useDataCache } from '../context/DataCacheContext';
import { CACHE_KEYS } from '../constants/cacheKeys';
import { useAuth } from '../context/AuthContext';

export function useFocusContent(focusId: string) {
  const { user } = useAuth();
  const { invalidateCache } = useDataCache();
  const [isLinking, setIsLinking] = useState(false);

  const linkContentBulk = async (contentIds: string[], metadata?: Record<string, any>) => {
    if (!user || !focusId || contentIds.length === 0) return false;

    setIsLinking(true);
    try {
      // Create content_links entries for each card
      const links = contentIds.map(cardId => ({
        source_type: 'career_card',
        source_id: cardId,
        target_type: 'career_focus',
        target_id: focusId,
        metadata: metadata || {}
      }));

      const { error } = await supabase
        .from('content_links')
        .insert(links);

      if (error) throw error;

      // Invalidate relevant caches
      await invalidateCache(`${CACHE_KEYS.GOAL_EVIDENCE}_${focusId}_*`);
      await invalidateCache(CACHE_KEYS.CAREER_CARDS);
      
      return true;
    } catch (error) {
      console.error('Error linking content:', error);
      return false;
    } finally {
      setIsLinking(false);
    }
  };

  const getLinkedContent = async () => {
    if (!user || !focusId) return [];

    try {
      const { data, error } = await supabase
        .from('content_links')
        .select('source_id')
        .eq('target_type', 'career_focus')
        .eq('target_id', focusId)
        .eq('source_type', 'career_card');

      if (error) throw error;

      return data?.map(link => link.source_id) || [];
    } catch (error) {
      console.error('Error fetching linked content:', error);
      return [];
    }
  };

  const unlinkContent = async (contentId: string) => {
    if (!user || !focusId) return false;

    try {
      const { error } = await supabase
        .from('content_links')
        .delete()
        .eq('source_type', 'career_card')
        .eq('source_id', contentId)
        .eq('target_type', 'career_focus')
        .eq('target_id', focusId);

      if (error) throw error;

      // Invalidate caches
      await invalidateCache(`${CACHE_KEYS.GOAL_EVIDENCE}_${focusId}_*`);
      await invalidateCache(CACHE_KEYS.CAREER_CARDS);
      
      return true;
    } catch (error) {
      console.error('Error unlinking content:', error);
      return false;
    }
  };

  return {
    linkContentBulk,
    getLinkedContent,
    unlinkContent,
    isLinking
  };
}